import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useFiscalCodeFlow } from '@vfit/consumer/hooks';
import { NameFiscalCode, PersonalInfo } from '@vfit/consumer/components';
import {
  ErrorDetail,
  getClickToCallWithKeyOrUrl,
  LoggerInstance,
  resetData,
  trackView,
} from '@vfit/shared/data-access';
import { API, getItemCookie } from '@vfit/shared/data-access';
import {
  CONSUMER_CMS_SHOPPING_CART,
  ErrorCodes,
  errorMock,
  getCurrentUserType,
  IGenericErrorCMS,
  IGlobalConfigCMS,
  IPersonalInfoCMS,
  isInResetUser,
  IUserType,
  PARTNER_OFFER_COOKIE,
  useCmsConfig,
} from '@vfit/consumer/data-access';
import { ICommonData, IPersonalDataObject } from '@vfit/shared/models';
import {
  checkError,
  getCmsValidationConfig,
  organizeNameFiscalCode,
  organizePersonalInfo,
  organizeRetriveFiscalCode,
  setNewCartCookie,
} from './nameFiscalCodeCard.utils';
import { ID_FLOWS } from '../../checkout.constants';
import { useCheckout } from '../../../iBuyFixed.context';
import { getTagging } from '../../checkout.utils';
import NameFiscalCodeCardTagging from './nameFiscalCodeCard.tagging';

const NameFiscalCodeCard = ({ handleOnChangeEnableGoNext, handleOnClose }: ICommonData) => {
  const {
    currentStepKey,
    checkoutErrors,
    owningData,
    findCaller,
    isLoadingCart,
    isModalButtonSticky,
    customerFlow,
    product,
    setOwningData,
    setFindCaller,
    setCurrentStepKey,
    serializeContext,
    deleteSerializedContext,
  } = useCheckout();
  const genericError = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GENERIC_ERROR
  ) as IGenericErrorCMS;
  const cookieSettingsCms = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG
  ) as IGlobalConfigCMS;
  const cartCookieHoursDuration: number = cookieSettingsCms?.globalconfig?.cookies?.cartCookie
    ?.durationHours
    ? parseFloat(cookieSettingsCms?.globalconfig?.cookies?.cartCookie?.durationHours || '2')
    : 48;
  const cartCookieDaysDuration = cartCookieHoursDuration / 24;
  const queryClient = useQueryClient();
  const isResetUser = isInResetUser();
  const isLoggedUser = getCurrentUserType() === IUserType.LOGGED_USER;

  const [isLoadingReset, setIsLoadingReset] = useState(isResetUser);
  const {
    isLoading: isLoadingFiscalCodeFlow,
    isError,
    errorCode,
    getFindCallerResponse,
    isInResetCustomer,
  } = useFiscalCodeFlow({
    fiscalCode: owningData.owningIndividual.fiscalCode,
    owningDataIsValid: isLoadingReset ? false : owningData.isValid || false,
    isFlowEnabled: !isLoadingCart && !isLoggedUser,
  });
  const customerTypeForRedirect: string[] = ['existingLegacyCustomer', 'inMigration'];
  const [isErrorFiscaleCode, setIsErrorFiscalCode] = useState('');
  const dataFromCms = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_PERSONAL_DATA
  ) as IPersonalInfoCMS;
  const { legacyPopup } = organizeNameFiscalCode(dataFromCms, product);
  const personalInfoConfigCms = organizePersonalInfo(dataFromCms);
  const nameFiscalCodeConfigCms = organizeRetriveFiscalCode(dataFromCms);
  const isPartnerOffer = (): boolean => product?.partnerOffer?.isPartnerOffer || false;
  const isLoading = isLoggedUser
    ? isLoadingFiscalCodeFlow || !customerFlow?.associateCustomer?.isSuccess
    : isLoadingFiscalCodeFlow;
  const taggingInfo = getTagging('personal data', 'fixed', queryClient);

  useEffect(() => {
    setCurrentStepKey(ID_FLOWS.PERSONAL_INFO);
    if (isResetUser) {
      setTimeout(() => {
        deleteSerializedContext();
        window.history.pushState(null, '', window.location.pathname);
        setIsLoadingReset(false);
      }, 1000);
    }
  }, []);

  const handleGoNext = () => {
    if (handleOnChangeEnableGoNext) {
      handleOnChangeEnableGoNext(owningData.isValid && !isLoading && !isLoadingCart);
    }
  };

  const resetQueries = () => {
    resetData(queryClient, [
      'fiscalCodeBirthPlace',
      'findCaller',
      'generateOTP',
      'verifyOTP',
      'postCustomer',
      'customerData',
      'updateProductCharacteristic',
      'validateDuplicateOrder',
      'payMean',
      'associateCustomer',
    ]);
  };

  useEffect(() => {
    if (getFindCallerResponse) {
      if (
        !findCaller.verifyOtp &&
        getFindCallerResponse.resultInfo &&
        customerTypeForRedirect.includes(getFindCallerResponse.resultInfo)
      ) {
        resetQueries();
      }
    }
  }, []);

  useEffect(() => {
    if (getFindCallerResponse) {
      LoggerInstance.debug('FIND CALLER RESPONSE', getFindCallerResponse);
      const showOtp =
        !owningData.isLogged &&
        !findCaller.isVerified &&
        !!(
          getFindCallerResponse.individualRef?.email || getFindCallerResponse.individualRef?.msisdn
        );
      setFindCaller({
        ...getFindCallerResponse,
        showOtp,
        isVerified: !!owningData.isLogged,
      });
      setNewCartCookie(getFindCallerResponse.resultInfo, product, cartCookieDaysDuration);
      if (
        !findCaller.verifyOtp &&
        getFindCallerResponse.resultInfo &&
        customerTypeForRedirect.includes(getFindCallerResponse.resultInfo)
      ) {
        const redirectAction = () => {
          if (legacyPopup?.legacyPopupUrl) {
            getClickToCallWithKeyOrUrl(
              queryClient,
              legacyPopup?.legacyPopupUrl,
              product,
              handleOnClose
            );
          } else if (handleOnClose) {
            handleOnClose();
          }
        };

        trackView({
          event_name: 'checkout_step1.3',
          event_label_track: 'coexistent popup',
        });
        checkoutErrors?.showAndTrackError?.(
          genericError,
          handleOnClose,
          undefined,
          legacyPopup?.legacyPopupTitle,
          legacyPopup?.legacyPopupMessage,
          redirectAction,
          legacyPopup?.legacyPopupActionText,
          undefined,
          undefined,
          isModalButtonSticky
        );
      }
    }
  }, [getFindCallerResponse]);

  useEffect(() => {
    if (isInResetCustomer && !isResetUser) {
      checkoutErrors?.showAndTrackError?.(
        genericError,
        () => {
          if (handleOnClose) handleOnClose();
        },
        errorMock('resetUser', {
          url: 'resetUser',
          status: '200',
          statusText: '200',
        }),
        'Attenzione!',
        "Vuoi effettuare l'ordine con un altro utente?",
        () => {
          localStorage.removeItem('customerDataCf');
          serializeContext(true);
          setTimeout(() => {
            if (handleOnChangeEnableGoNext) {
              handleOnChangeEnableGoNext(false);
            }
            const href = `${window.location.href}?${ErrorCodes.MODE_RESET_USER}`;
            LoggerInstance.debug('Redirect reset', href);
            window.location.href = href;
          }, 1500);
        },
        'Si',
        () => {
          if (handleOnClose) handleOnClose();
        },
        'No',
        isModalButtonSticky,
        true
      );
    }
  }, [isInResetCustomer]);

  useEffect(() => {
    handleGoNext();
  }, [isLoading, owningData.isLogged, owningData.isValid]);

  useEffect(() => {
    if (isError) {
      const isFiscalCodeError = errorCode === ErrorCodes.FISCAL_CODE_ERROR;
      const errorDetail: ErrorDetail = checkError(
        errorCode,
        handleOnClose,
        genericError?.genericerror
      );
      setOwningData({
        ...owningData,
        owningIndividual: {
          ...owningData.owningIndividual,
          fiscalCode:
            errorCode === ErrorCodes.FISCAL_CODE_ERROR
              ? owningData.owningIndividual.fiscalCode
              : '',
        },
        isValid: false,
      });
      resetQueries();

      if (errorCode !== ErrorCodes.FISCAL_CODE_ERROR) {
        setIsErrorFiscalCode('');
      } else {
        setIsErrorFiscalCode(() => errorDetail?.message || '');
      }

      checkoutErrors?.showAndTrackError?.(
        genericError,
        errorDetail?.onClose,
        errorMock('checkFiscalCode', {
          url: 'checkFiscalCode',
          status: 'NA',
          statusText: errorCode || 'error',
        }),
        errorDetail?.title,
        errorDetail?.message,
        errorDetail?.onClose,
        errorDetail.actionText,
        () =>
          getClickToCallWithKeyOrUrl(
            queryClient,
            product?.genericErrorCallMeNow?.url || '',
            product,
            handleOnClose,
            currentStepKey,
            errorMock('checkFiscalCode', {
              url: 'checkFiscalCode',
              status: 'NA',
              statusText: errorCode || 'error',
            })
          ),
        product?.genericErrorCallMeNow?.label || '',
        isModalButtonSticky,
        true,
        isFiscalCodeError
      );
    }
  }, [isError]);

  const getPartnerOfferData = (): IPersonalDataObject => {
    const partnerOffer = getItemCookie(PARTNER_OFFER_COOKIE);
    if (!partnerOffer) {
      checkoutErrors?.showAndTrackError?.(
        genericError,
        handleOnClose,
        errorMock('partnerOffer', {
          url: 'getPartnerOfferData',
          status: '500',
          statusText: '500',
        }),
        legacyPopup?.legacyPopupTitle,
        legacyPopup?.legacyPopupMessage,
        handleOnClose,
        legacyPopup?.legacyPopupActionText,
        () =>
          getClickToCallWithKeyOrUrl(
            queryClient,
            product?.genericErrorCallMeNow?.url || '',
            product,
            handleOnClose,
            currentStepKey,
            errorMock('partnerOffer', {
              url: 'getPartnerOfferData',
              status: '500',
              statusText: '500',
            })
          ),
        product?.genericErrorCallMeNow?.label || '',
        isModalButtonSticky,
        true
      );
    }
    return {
      ...owningData,
      owningIndividual: {
        ...owningData?.owningIndividual,
        firstName: partnerOffer?.firstName || '',
        lastName: partnerOffer?.lastName || '',
        fiscalCode: partnerOffer?.fiscalCode || '',
      },
    };
  };

  const onChangeData = (data: IPersonalDataObject, isValid?: boolean) => {
    const { owningIndividual: owningIndividualData } = data;
    const {
      firstName: firstNameData,
      lastName: lastNameData,
      fiscalCode: fiscalCodeData,
    } = owningIndividualData;
    const { owningIndividual } = owningData;
    const { firstName, lastName, fiscalCode } = owningIndividual;
    if (owningData) {
      setOwningData({
        ...data,
      });
    }

    if (firstNameData !== firstName || lastNameData !== lastName || fiscalCodeData !== fiscalCode) {
      resetQueries();
      if (!isValid) {
        setOwningData({
          ...data,
          isValid: false,
          isLogged: false,
        });
      }
    }
  };

  const getData = () => {
    if (isPartnerOffer()) return getPartnerOfferData();
    return owningData;
  };

  return (
    <div>
      <NameFiscalCodeCardTagging />
      {(owningData.isLogged || isLoggedUser) && (
        <PersonalInfo data={owningData} isLoading={isLoading} cmsConfig={personalInfoConfigCms} />
      )}
      {!owningData.isLogged && !isLoadingReset && !isLoggedUser && (
        <NameFiscalCode
          errorFiscalCode={
            !owningData.isValid && isErrorFiscaleCode ? isErrorFiscaleCode : undefined
          }
          data={getData()}
          onChangeData={onChangeData}
          isLoading={owningData?.isValid && isLoadingCart ? true : isLoading}
          cmsConfig={nameFiscalCodeConfigCms}
          taggingData={{
            trackingProduct: taggingInfo.trackingProduct,
            opts: taggingInfo.opts,
          }}
          isFormDisabled={product?.isAcceptedFamilyPlusProduct || isPartnerOffer()}
          cmsValidationConfig={getCmsValidationConfig(dataFromCms)}
        />
      )}
    </div>
  );
};

export default NameFiscalCodeCard;

